import { peekFitnessGuidance } from '@/i18n/translations';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { detectLangFromTags, normalizeLang } from '@/i18n/locale';

/** Original static instruction for fifteen exact arm/shoulder variants.
 * Sources and explicit product transfers are recorded in
 * docs/research/fitness-rea-20261015/guidance-drafts/arm15/sources.json.
 * The phased 21s demonstration is not user repetition tracking or an interval plan.
 */
export const ARM_EXERCISE_GUIDANCE_IDS = [
  "standing-bilateral-dumbbell-front-raise",
  "bent-over-bilateral-dumbbell-rear-delt-raise",
  "standing-barbell-upright-row",
  "standing-dumbbell-shrug",
  "seated-ez-bar-preacher-curl",
  "seated-one-arm-dumbbell-concentration-curl",
  "standing-barbell-21s-curl",
  "standing-low-cable-straight-bar-curl",
  "standing-low-cable-rope-curl",
  "standing-rope-triceps-pushdown",
  "seated-two-hand-dumbbell-overhead-extension",
  "standing-straight-bar-triceps-pressdown",
  "bent-knee-bench-dip",
  "seated-arnold-press",
  "close-grip-bench-press"
] as const;
export type ArmExerciseGuidanceId = typeof ARM_EXERCISE_GUIDANCE_IDS[number];
type GuidanceCopy = readonly [string, string, string, string, string];
export type ArmExerciseGuidanceDictionary = Record<ArmExerciseGuidanceId, GuidanceCopy>;
/** Exact selected app identities stay separate from authored contract IDs. */
export const ARM_GUIDANCE_MOVEMENT_BINDINGS = [
  [
    "free-shoulders-elevacao-frontal",
    "standing-bilateral-dumbbell-front-raise"
  ],
  [
    "free-shoulders-elevacao-posterior",
    "bent-over-bilateral-dumbbell-rear-delt-raise"
  ],
  [
    "free-shoulders-remada-alta",
    "standing-barbell-upright-row"
  ],
  [
    "free-shoulders-encolhimento",
    "standing-dumbbell-shrug"
  ],
  [
    "free-biceps-rosca-scott",
    "seated-ez-bar-preacher-curl"
  ],
  [
    "free-biceps-rosca-concentrada",
    "seated-one-arm-dumbbell-concentration-curl"
  ],
  [
    "free-biceps-rosca-21",
    "standing-barbell-21s-curl"
  ],
  [
    "free-biceps-rosca-polia",
    "standing-low-cable-straight-bar-curl"
  ],
  [
    "free-biceps-rosca-cabo",
    "standing-low-cable-rope-curl"
  ],
  [
    "free-triceps-corda",
    "standing-rope-triceps-pushdown"
  ],
  [
    "free-triceps-frances",
    "seated-two-hand-dumbbell-overhead-extension"
  ],
  [
    "standing-straight-bar-triceps-pressdown",
    "standing-straight-bar-triceps-pressdown"
  ],
  [
    "free-triceps-banco",
    "bent-knee-bench-dip"
  ],
  [
    "seated-arnold-press",
    "seated-arnold-press"
  ],
  [
    "close-grip-bench-press",
    "close-grip-bench-press"
  ]
] as const;
const byMovement = new Map<string, ArmExerciseGuidanceId>(ARM_GUIDANCE_MOVEMENT_BINDINGS);

/** No display, provider or approximate variant alias is inferred. */
export function getArmExerciseGuidance(exerciseName: string, lang?: string): { steps: string[]; cues: string[] } | null {
  const id = getExplicitFitnessMovement(exerciseName)?.id;
  const key = id ? byMovement.get(id) : undefined;
  if (!key) return null;
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const copy = peekFitnessGuidance(language, 'arm', key);
  if (!copy) return null;
  return { steps: copy.slice(0, 3), cues: copy.slice(3) };
}
